import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "455",
  "slug": "what-is-a-canyon-how-canyons-form",
  "title": "What Is a Canyon? How Rivers Carve the World’s Deepest Gorges",
  "excerpt": "A canyon is a deep, steep-sided valley cut by running water over vast stretches of time. Here is the recipe behind them and a tour of the deepest gorges on Earth.",
  "date": "2026-06-16",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1575527048208-6475b441e0a0?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxncmFuZCUyMGNhbnlvbiUyMHJpdmVyfGVufDF8MHx8fDE3ODE1OTc2NjR8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A canyon is a deep valley with steep rock walls, usually carved by a river slowly cutting downward while the surrounding land is uplifted. The Grand Canyon is the classic example, but the deepest is the Yarlung Tsangpo in Tibet.",
  "content": [
    {
      "type": "paragraph",
      "text": "A canyon is a deep, narrow valley with steep, often sheer rock walls, carved over enormous spans of time by running water. The word is sometimes used interchangeably with gorge, and the two describe the same basic feature: a place where a river has sliced downward through solid rock, leaving cliffs towering on either side."
    },
    {
      "type": "paragraph",
      "text": "Canyons are among the most awe-inspiring landscapes on Earth, and they are also open textbooks. Their exposed walls lay bare millions of years of geologic history in neat, readable layers."
    },
    {
      "type": "heading",
      "text": "The Recipe for a Canyon"
    },
    {
      "type": "paragraph",
      "text": "Carving a great canyon takes a specific combination of ingredients working together over millions of years."
    },
    {
      "type": "list",
      "items": [
        "A river with energy — flowing water armed with sand and gravel acts like liquid sandpaper, grinding downward into the bedrock.",
        "Uplift — the land itself rises, steepening the river’s gradient and giving it the power to keep cutting down rather than spreading out.",
        "A dry climate — with little rain, the canyon walls erode back slowly, so the gorge stays narrow and steep instead of widening into a broad valley.",
        "Layered rock — alternating hard and soft strata erode at different rates, producing the stepped, sculpted profiles canyons are famous for."
      ]
    },
    {
      "type": "heading",
      "text": "The Grand Canyon, and the Ones That Beat It"
    },
    {
      "type": "paragraph",
      "text": "The Grand Canyon in Arizona is the canyon most people picture. The Colorado River carved it into the Colorado Plateau, creating a gorge roughly 446 kilometres long and up to about 1.8 kilometres deep, with walls that expose nearly two billion years of Earth’s history. It is a masterclass in everything a canyon can be."
    },
    {
      "type": "paragraph",
      "text": "Yet it is not the deepest. That title is usually given to the Yarlung Tsangpo Grand Canyon in Tibet, where the river plunges through the Himalayas to depths of more than 5,000 metres. In Peru, the Colca and Cotahuasi canyons are also deeper than the Grand Canyon, and Namibia’s Fish River Canyon is one of the largest in Africa."
    },
    {
      "type": "heading",
      "text": "Not All Canyons Are Alike"
    },
    {
      "type": "paragraph",
      "text": "Canyons come in several distinct varieties, each shaped by its own process."
    },
    {
      "type": "list",
      "items": [
        "River canyons — the classic type, cut by a permanent river over long stretches of time.",
        "Slot canyons — extremely narrow, deep channels carved by flash floods through soft rock, like Antelope Canyon in Arizona.",
        "Box canyons — short canyons with steep walls on three sides and only one way in or out.",
        "Submarine canyons — vast gorges that cut into the continental shelf beneath the ocean, often offshore from major rivers."
      ]
    },
    {
      "type": "heading",
      "text": "Canyon, Valley, or Gorge?"
    },
    {
      "type": "paragraph",
      "text": "It is worth untangling some overlapping words. A valley is the broad, general term for any low area between higher land; many valleys are wide and gentle, carved by glaciers or slow rivers. A canyon or gorge is a specific kind of valley: deep, narrow, and steep-walled, the product of a powerful river cutting fast through rock that resists eroding back. In short, every canyon is a valley, but very few valleys are canyons."
    },
    {
      "type": "heading",
      "text": "Old Rock, Young Canyon"
    },
    {
      "type": "paragraph",
      "text": "One of the great surprises of canyons is the gap between the age of the rock and the age of the gorge. The walls of the Grand Canyon expose rock formed over nearly two billion years, yet the canyon itself is young by geologic standards. The Colorado River is thought to have carved most of it within just the last several million years, and the exact timeline is still debated by geologists. A canyon is essentially a fresh wound cut into very old stone."
    },
    {
      "type": "paragraph",
      "text": "Canyons are not unique to Earth, either. The largest known canyon in the solar system is Valles Marineris on Mars, a system of gorges that stretches more than 4,000 kilometres and dwarfs anything on our planet. Studying how canyons form here helps scientists read the history written into other worlds, and it underlines a humbling point: the deepest cuts on any planet are usually carved not by sudden catastrophe but by the patient, relentless work of moving water and time."
    },
    {
      "type": "heading",
      "text": "Spotting a Canyon From Above"
    },
    {
      "type": "paragraph",
      "text": "From a satellite view, a canyon appears as a dark, branching scar threading across an otherwise flat or elevated landscape. Look for a sinuous line with a thin ribbon of river at its bottom, flanked by the intricate, tree-like network of side gorges that feed into the main channel. Shadows along the walls reveal just how deep the cut really goes."
    },
    {
      "type": "paragraph",
      "text": "Learning to read those shadows and drainage patterns is exactly the skill that makes a great location-guesser. Test yours in EarthGuessr and see if you can place a canyon from the air."
    }
  ]
};
